import { useState, useEffect, useMemo } from "react";
import { authenticatedFetch } from "../services/auth";
import { API_BASE } from "../utils/apiConfig";

export const useBSCData = (shouldExecute = true) => {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState("none");

  useEffect(() => {
    // Só executar se shouldExecute for true
    if (!shouldExecute) {
      setLoading(false);
      setIndicators([]);
      setError(null);
      setDataSource("none");
      return;
    }
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    let retryTimeout = null;
    let isExecuting = false; // Proteção contra execuções simultâneas

    const fetchDataFromAPI = async () => {
      try {
        if (!isMounted || isExecuting) return;

        isExecuting = true; // Marcar como em execução
        setLoading(true);
        setError(null);

        console.log("Tentando conectar com a API...");

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          if (isMounted) {
            controller.abort();
          }
        }, 10000);

        try {
          const response = await authenticatedFetch(
            `${API_BASE}/api/indicators`,
            {
              signal: controller.signal,
              headers: {
                "Content-Type": "application/json",
              },
            },
          );

          if (!isMounted) return;
          clearTimeout(timeoutId);

          if (response.status === 404) {
            console.log("⚠️ API retornou 404 - API offline");
            setError("API offline - Serviço temporariamente indisponível");
            setIndicators([]);
            setDataSource("none");
            return;
          }

          if (!response.ok) {
            throw new Error(`Erro HTTP: ${response.status}`);
          }

          const data = await response.json();

          if (!isMounted) return;

          if (data === null || data === undefined) {
            console.log("⚠️ API retornou dados nulos");
            setError(null);
            setIndicators([]);
            setDataSource("api");
            return;
          }

          if (!Array.isArray(data)) {
            console.warn("⚠️ API retornou formato não esperado:", typeof data);
            setError(null);
            setIndicators([]);
            setDataSource("api");
            return;
          }

          if (data.length === 0) {
            console.log("⚠️ API retornou dados vazios - sistema sem dados cadastrados");
            setError(null);
            setIndicators([]);
            setDataSource("api");
            return;
          }

          console.log("✅ Dados carregados da API com sucesso");
          setIndicators(data);
          setDataSource("api");
          setError(null);
          retryCount = 0; // Reset retry count on success
        } catch (apiError) {
          if (!isMounted) return;

          clearTimeout(timeoutId);
          console.error("❌ Erro na API:", apiError);

          if (apiError.name === "AbortError") {
            setError("Tempo limite de conexão excedido - API offline");
          } else if (apiError.message.includes("fetch") || apiError.message.includes("conexão")) {
            setError("API offline - Não foi possível conectar ao servidor");
          } else {
            setError(`Erro na API: ${apiError.message}`);
          }

          setIndicators([]);
          setDataSource("none");

          // LIMITAR TENTATIVAS PARA EVITAR LOOP INFINITO
          if ((apiError.message.includes("fetch") || apiError.message.includes("conexão")) &&
            retryCount < maxRetries && isMounted) {
            retryCount++;
            console.log(`⏳ Tentativa ${retryCount}/${maxRetries} em 10 segundos...`);
            retryTimeout = setTimeout(() => {
              if (isMounted) {
                fetchDataFromAPI();
              }
            }, 10000); // Aumentar delay para 10 segundos
            return;
          }

          // SE ESGOTOU AS TENTATIVAS, PARAR
          if (retryCount >= maxRetries) {
            console.log("❌ Máximo de tentativas excedido. Parando tentativas automáticas.");
            setError("API offline - Máximo de tentativas excedido");
          }
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("❌ Erro crítico ao carregar dados:", error);
        setError("Sistema temporariamente indisponível");
        setIndicators([]);
        setDataSource("none");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
        isExecuting = false; // Liberar execução
      }
    };

    // Executar fetch apenas uma vez
    fetchDataFromAPI().catch((error) => {
      if (isMounted) {
        console.error("Erro final capturado:", error);
        setError("Sistema temporariamente indisponível");
        setIndicators([]);
        setDataSource("none");
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [shouldExecute]); // Adicionar shouldExecute como dependência

  const perspectives = useMemo(() => {
    return [...new Set(indicators.map((i) => i.perspective))];
  }, [indicators]);

  const indicatorTypes = useMemo(() => {
    return [...new Set(indicators.map((i) => i.indicator))];
  }, [indicators]);

  const kpiSummary = useMemo(() => {
    const result = {};
    for (const type of indicatorTypes) {
      const typeData = indicators
        .filter((ind) => ind.indicator === type)
        .sort((a, b) => new Date(b.period) - new Date(a.period));

      const latest = typeData[0];
      const previous = typeData[1];

      if (latest) {
        result[type] = {
          latest,
          trend: previous
            ? latest.actual > previous.actual
              ? "up"
              : latest.actual < previous.actual
                ? "down"
                : "neutral"
            : "neutral",
          change: previous
            ? (
              ((latest.actual - previous.actual) / previous.actual) *
              100
            ).toFixed(1)
            : "0",
        };
      }
    }
    return result;
  }, [indicators, indicatorTypes]);

  return {
    indicators,
    setIndicators,
    loading,
    error,
    perspectives,
    indicatorTypes,
    kpiSummary,
    dataSource,
  };
};